import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { StatusPill } from '@/components/cccd/StatusPill';
import { DraggableRow, DragHandle } from '@/components/cccd/DraggableRow';
import { useDragReorder } from '@/hooks/useDragReorder';
import { btnPrimary, btnSecondary } from '@/components/admin/FormComponents';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import Checkbox from '@/components/form/input/Checkbox';
import { TrashBinIcon, PlusIcon } from '@/icons';
import { cccdScoring, type CategoryEvaluationParameter } from '@/lib/api/cccdScoringClient';
import { PARAMETER_TYPE_LABELS } from '@/lib/cccdScoringParameters';
import { ParameterConfigFields, EMPTY_PARAMETER_CONFIG, toParameterPayload, type ParameterConfigValues } from '@/components/cccd/ParameterConfigFields';
import { toastError, toastSuccess } from '@/lib/toast';
import { useState } from 'react';

const ITEM_TYPE = 'cccd-category-parameter-row';

// Qué parámetros (ya definidos en la biblioteca global, /cccd/scoring-
// parameters) evalúa ESTA categoría, y en qué orden — cccd-scoring-global-
// parameters: aquí solo se VINCULAN parámetros existentes, nunca se crean
// ni se recrean por evento. Vive en la página de la categoría (nacional o
// regional, el vínculo es genérico por categoryId), no en el detalle del
// evento — un parámetro se configura una vez por categoría, no una vez por
// pestaña Puntaje.
//
// cccd-scoring-ux-redesign, spec cccd-scoring-parameter-setup: vincular
// varios parámetros de una vez (loop secuencial sobre el endpoint existente
// de a uno — ver design.md decisión 5, no se agregó un endpoint bulk), y
// crear un parámetro nuevo sin salir de esta sección.
export function ScoringParametersSection({ categoryId }: { categoryId: string }) {
  const qc = useQueryClient();
  const { data: parameters = [] } = useQuery({ queryKey: ['scoring-parameters'], queryFn: () => cccdScoring.listParameters() });

  const categoryLinksKey = ['scoring-category-parameters', categoryId];
  const { data: categoryLinks = [] } = useQuery({
    queryKey: categoryLinksKey,
    queryFn: () => cccdScoring.listCategoryParameters(categoryId),
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [failedIds, setFailedIds] = useState<string[]>([]);
  const [isLinking, setIsLinking] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<CategoryEvaluationParameter | null>(null);
  const [creating, setCreating] = useState(false);
  const [createValues, setCreateValues] = useState<ParameterConfigValues>(EMPTY_PARAMETER_CONFIG);

  const invalidateCategoryLinks = () => qc.invalidateQueries({ queryKey: categoryLinksKey });
  const invalidateParameters = () => qc.invalidateQueries({ queryKey: ['scoring-parameters'] });

  const unlinkMutation = useMutation({
    mutationFn: (parameterId: string) => cccdScoring.unlinkCategoryParameter(categoryId, parameterId),
    onSuccess: () => {
      setUnlinkTarget(null);
      invalidateCategoryLinks();
      // El total de la Clasificación suma solo los parámetros vinculados: cambia al quitar uno.
      qc.invalidateQueries({ queryKey: ['category-leaderboard'] });
    },
    onError: (err: any) => {
      setUnlinkTarget(null);
      toastError(err?.response?.data?.error ?? 'No se pudo desvincular el parámetro.');
    },
  });

  const { moveRow, onDragStart, onDrop } = useDragReorder<CategoryEvaluationParameter>(
    categoryLinksKey,
    (parameterId, orderIndex) => cccdScoring.reorderCategoryParameter(categoryId, parameterId, orderIndex),
  );

  const createMutation = useMutation({
    mutationFn: () => cccdScoring.createParameter(toParameterPayload(createValues)),
    onSuccess: (created) => {
      setCreating(false);
      setCreateValues(EMPTY_PARAMETER_CONFIG);
      setSelectedIds((prev) => [...prev, created.id]);
      toastSuccess('Parámetro creado — ya está seleccionado para vincular.');
      invalidateParameters();
    },
    onError: (err: any) => toastError(err?.response?.data?.error ?? 'No se pudo crear el parámetro.'),
  });

  const linkedIds = new Set(categoryLinks.map((l) => l.id));
  const unlinkedParameters = parameters.filter((p) => !linkedIds.has(p.id));

  function toggleSelected(id: string) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  // Vincula todos los ids seleccionados en el orden en que fueron elegidos,
  // uno por uno (el endpoint existente solo acepta un parámetro a la vez —
  // ver design.md decisión 5). Si alguno falla, se reporta cuáles y quedan
  // pre-seleccionados para reintentar solo esos, en vez de perder los que sí
  // se vincularon o bloquear el resto por un fallo parcial.
  async function linkSelected() {
    setIsLinking(true);
    const failed: string[] = [];
    let nextOrderIndex = categoryLinks.length;
    for (const id of selectedIds) {
      try {
        await cccdScoring.linkCategoryParameter(categoryId, id, nextOrderIndex);
        nextOrderIndex += 1;
      } catch {
        failed.push(id);
      }
    }
    setIsLinking(false);
    invalidateCategoryLinks();
    if (failed.length === 0) {
      setSelectedIds([]);
      setFailedIds([]);
      toastSuccess('Parámetros vinculados.');
    } else {
      setSelectedIds(failed);
      setFailedIds(failed);
      const names = parameters.filter((p) => failed.includes(p.id)).map((p) => p.name).join(', ');
      toastError(`No se pudieron vincular: ${names}. Siguen seleccionados para reintentar.`);
    }
  }

  return (
    <SectionHeader
      title="Parámetros de evaluación"
      subtitle="Qué parámetros evalúa esta categoría, y en qué orden aparecen al capturar resultados"
      icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></svg>}
    >
      {categoryLinks.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 dark:border-gray-700">
          Esta categoría no tiene parámetros vinculados todavía.
        </p>
      ) : (
        <table className="w-full rounded-xl border border-gray-100 text-sm dark:border-gray-800">
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {categoryLinks.map((link, index) => (
              <DraggableRow key={link.id} id={link.id} index={index} itemType={ITEM_TYPE} moveRow={moveRow} onDragStart={onDragStart} onDrop={onDrop}>
                <td className="w-8 py-2.5 pl-3"><DragHandle /></td>
                <td className="px-2.5 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <p className="text-[13px] font-medium text-gray-800 dark:text-white/90">{link.name}</p>
                    <StatusPill tone="gray" label={PARAMETER_TYPE_LABELS[link.parameterType]} />
                  </div>
                </td>
                <td className="w-10 py-2.5 pr-2 text-right">
                  <button type="button" onClick={() => setUnlinkTarget(link)} title="Quitar de esta categoría" aria-label={`Quitar ${link.name} de esta categoría`} className="flex size-11 items-center justify-center rounded-lg text-gray-500 hover:bg-error-50 hover:text-error-600 sm:size-8 dark:text-gray-400 dark:hover:bg-error-500/10">
                    <TrashBinIcon className="size-4" />
                  </button>
                </td>
              </DraggableRow>
            ))}
          </tbody>
        </table>
      )}

      {unlinkedParameters.length > 0 && (
        <div className="mt-3.5 rounded-xl border border-gray-100 p-3 dark:border-gray-800">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Vincular parámetros existentes</p>
          <div className="flex flex-col gap-1">
            {unlinkedParameters.map((p) => {
              const isSelected = selectedIds.includes(p.id);
              const isFailed = failedIds.includes(p.id);
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-colors ${
                    isFailed
                      ? 'bg-error-50 dark:bg-error-500/10'
                      : isSelected
                        ? 'bg-brand-50 dark:bg-brand-500/[0.08]'
                        : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'
                  }`}
                >
                  <Checkbox checked={isSelected} onChange={() => toggleSelected(p.id)} />
                  <button type="button" onClick={() => toggleSelected(p.id)} className="flex flex-1 items-center gap-2.5 text-left">
                    <span className={isSelected ? 'font-medium text-gray-800 dark:text-white/90' : 'text-gray-700 dark:text-gray-300'}>{p.name}</span>
                    <StatusPill tone="gray" label={PARAMETER_TYPE_LABELS[p.parameterType]} />
                  </button>
                  {isFailed && <StatusPill tone="error" label="Reintentar" />}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            disabled={selectedIds.length === 0 || isLinking}
            onClick={linkSelected}
            className={`${btnPrimary} mt-3`}
          >
            {isLinking ? 'Vinculando...' : `Vincular seleccionados (${selectedIds.length})`}
          </button>
        </div>
      )}

      {!creating ? (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mt-3.5 flex w-full items-center gap-2.5 rounded-[14px] border-[1.5px] border-dashed border-gray-300 bg-gray-25 px-3.5 py-3 text-gray-400 transition-colors hover:border-brand-300 hover:text-brand-500 dark:border-gray-700 dark:bg-white/[0.02] dark:hover:border-brand-700"
        >
          <PlusIcon className="size-4" />
          <span className="text-[13px] font-medium">Crear un parámetro nuevo</span>
        </button>
      ) : (
        <div className="mt-3.5 rounded-2xl border border-brand-200 bg-brand-50/40 p-4 dark:border-brand-800 dark:bg-brand-500/5">
          <ParameterConfigFields values={createValues} onChange={setCreateValues} />
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={() => { setCreating(false); setCreateValues(EMPTY_PARAMETER_CONFIG); }}>
              Cancelar
            </button>
            <button
              type="button"
              className={btnPrimary}
              disabled={!createValues.name.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? 'Guardando...' : 'Crear parámetro'}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={unlinkTarget !== null}
        title="¿Quitar este parámetro de la categoría?"
        message={`Se quitará "${unlinkTarget?.name ?? ''}" de esta categoría: el total de la Clasificación se calculará sin este parámetro. Los resultados ya capturados no se borran y puedes volver a vincularlo.`}
        confirmLabel="Quitar parámetro"
        confirmingLabel="Quitando..."
        confirming={unlinkMutation.isPending}
        onConfirm={() => unlinkTarget && unlinkMutation.mutate(unlinkTarget.id)}
        onCancel={() => !unlinkMutation.isPending && setUnlinkTarget(null)}
      />

      <p className="mt-3.5 text-xs text-gray-500 dark:text-gray-400">
        ¿Necesitas editar un parámetro existente?{' '}
        <Link to="/cccd/scoring-parameters" className="text-brand-500 hover:underline">Administra la biblioteca de parámetros</Link>.
      </p>
    </SectionHeader>
  );
}
